/**
 * Shapes and helpers for the address registry that the pages share.
 *
 * The canonical row types (`Address`, `AddressSummary`, `AddressRequest`,
 * `HouseholdClaim`) live in src/types.ts next to the rest of the API shapes.
 * What is here is what only the frontend needs: the admin joins, the labels the
 * pages print, and the copy for the household branch.
 */
import type { Address, AddressSummary, AddressRequest, AccountStatus, TenureType } from '../types';

export type HouseholdRole = 'primary' | 'member';

/** One resident as `GET /api/admin/addresses` hands them back with the address. */
export interface AddressResident {
    id: string;
    full_name: string;
    email: string;
    phone?: string | null;
    tenure_type: TenureType;
    account_status: AccountStatus;
    household_role: HouseholdRole | null;
    resident_since: string;
}

/** One row of `GET /api/admin/addresses`. */
export interface AdminAddress extends Address {
    /** Formatted by the server; `addressLabel()` rebuilds it when absent. */
    label?: string;
    registered?: boolean;
    residents: AddressResident[];
    pending_requests: number;
}

export interface AddressCounts {
    total: number;
    occupied: number;
    gaps: number;
    pending_requests: number;
}

/** Where one page of `GET /api/admin/addresses` sits in the whole list. */
export interface AddressPage {
    limit: number;
    offset: number;
    returned: number;
    matched: number;
    has_more: boolean;
}

export interface AdminAddressesResponse {
    addresses: AdminAddress[];
    counts: AddressCounts;
    page?: AddressPage;
}

/**
 * One row of `GET /api/admin/address-requests`.
 *
 * The server shapes it with the address and the requester already joined, so
 * the panel never has to look anything else up.
 */
export interface AdminAddressRequest extends Omit<AddressRequest, 'address_id' | 'user_id'> {
    address: Omit<AddressSummary, 'registered'>;
    requester: { id: string; full_name: string; email: string; phone: string | null; account_status: AccountStatus };
    /** Who is registered at the address today, for the "who moves out" line. */
    current_residents: { id: string; full_name: string; account_status: AccountStatus }[];
}

export type AddressFilter = 'all' | 'occupied' | 'gaps' | 'pending';

/** "12A New Forest Road" / "Unit 3, 12 New Forest Road". */
export function addressLabel(a: Pick<Address, 'street_number' | 'street_name' | 'unit'>): string {
    const base = `${a.street_number} ${a.street_name}`.trim();
    return a.unit ? `Unit ${a.unit}, ${base}` : base;
}

/** The label the server sent, or one built from the parts. */
export function summaryLabel(a: AddressSummary): string {
    return a.label || addressLabel(a);
}

export const TENURE_LABEL: Record<TenureType, string> = { own: 'Owner', rent: 'Tenant' };

export const HOUSEHOLD_ROLE_LABEL: Record<HouseholdRole, string> = {
    primary: 'Main contact',
    member: 'Household member',
};
